import React from "react";
import { useFormik } from "formik";
import { Button, useMediaQuery, useTheme } from "@material-ui/core";
import { useIntl } from "react-intl";
import DialogTitle from "@material-ui/core/DialogTitle";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import { useSnackbar } from "notistack";
import { messages } from "./UpdateUsernameDialog.messages";
import { useAuthenticatedUser } from "../../../Providers/AuthenticatedUserProvider/AuthenticatedUserProvider";
import { useCustomerId } from "../../../Providers/CustomerProvider/CustomerProvider";
import { userService } from "../../../Providers/ServiceProvider/ServiceProvider";
import UpdateUsernameValidationSchema from "../../Fields/UpdateUsernameFields/Components/Validation/UpdateUsernameValidationSchema";
import UpdateUsernameFields, {
  UpdateUsernameFormikValues,
} from "../../Fields/UpdateUsernameFields/UpdateUsernameFields";

interface Props {
  userId: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdated?: () => void;
}

const UpdateUsernameDialog = (props: Props) => {
  const { userId, isOpen, onClose, onUpdated } = props;
  const intl = useIntl();
  const theme = useTheme();
  const customerId = useCustomerId();
  const { enqueueSnackbar } = useSnackbar();
  const [authenticatedUser, setAuthenticatedUser] = useAuthenticatedUser();
  const fullScreen = useMediaQuery(theme.breakpoints.down("xs"));

  const initialValues = {
    newUsername: "",
    password: "",
  } as UpdateUsernameFormikValues;

  const formik = useFormik({
    initialValues,
    validationSchema: UpdateUsernameValidationSchema,
    onSubmit: (values) => {
      if (customerId && authenticatedUser?.user) {
        userService()
          .updateUsername({
            userId,
            updateUsernameRequest: {
              username: values.newUsername,
              password: values.password,
            },
          })
          .then((res) => {
            setAuthenticatedUser({
              ...authenticatedUser,
              user: res,
              isComplete: true,
            });
            enqueueSnackbar(intl.formatMessage(messages.success), {
              variant: "success",
            });
          })
          .then(onUpdated)
          .catch(() =>
            enqueueSnackbar(intl.formatMessage(messages.generalError), {
              variant: "error",
            })
          )
          .finally(onClose);
      }
    },
  });

  return (
    <Dialog
      onClick={(e) => e.stopPropagation()}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="xs"
      open={isOpen}
      onClose={onClose}
      aria-labelledby="Update-username-dialog"
    >
      <DialogTitle id="form-dialog-title">
        {intl.formatMessage(messages.dialogTitle)}
      </DialogTitle>
      <form onSubmit={formik.handleSubmit}>
        <DialogContent>
          <UpdateUsernameFields formik={formik} />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" type="submit" color="primary">
            {intl.formatMessage(messages.submitButtonLabel)}
          </Button>
          <Button onClick={onClose} color="primary">
            {intl.formatMessage(messages.cancelButtonLabel)}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UpdateUsernameDialog;
